import React, { FC, useMemo } from 'react';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { OnChangeValue, StylesConfig } from 'react-select';
import { SelectOption } from '@qwealth/qdata';

import { useUserList } from 'hooks/useUserList';
import { useAppSelector } from 'data/store';
import { getPreviouslyCachedSelected, updateCachedOptions } from './helper';

let prevSearch;

const fields = ['firstName', 'lastName', 'email', 'QID'];

const searchFilter = query => user =>
  query.split(' ').every(term => fields.some(attribute => user[attribute] && user[attribute].toLowerCase().includes(term)));

interface Props {
  disabled?: boolean;
  isMulti?: boolean;
  onBlur?: () => void;
  value: SelectOption<string> | Array<SelectOption<string>> | string | null;
  onChange: (selected: SelectOption<string> | Array<SelectOption<string>> | null, actionMeta) => void;
  customStyles?: StylesConfig;
  isClearable?: boolean;
  menuPortalTarget?: HTMLElement;
  menuPlacement?: 'auto' | 'bottom' | 'top';
}

const ContactSelect: FC<Props> = ({
  value,
  onChange,
  onBlur = () => {},
  disabled = false,
  isMulti = false,
  customStyles,
  isClearable = false,
  menuPortalTarget,
  menuPlacement = 'auto',
}): JSX.Element => {
  const team = useAppSelector(state => state.team);
  const userList = useUserList();

  const teamOptions = useMemo(() => {
    const options: SelectOption<string>[] = getPreviouslyCachedSelected();
    const emails: string[] = options.map(o => o.value);
    const names: string[] = options.map(o => o.label);
    team.forEach(code => {
      const { email } = code;
      const teamMemberName = code.teamMemberName ?? '';
      // prevent duplicates AND bad options (missing email/teamMemberName)
      if (!emails.includes(email) && !names.includes(teamMemberName) && teamMemberName && email) {
        emails.push(email);
        names.push(teamMemberName);
        options.push({ value: email, label: teamMemberName });
      }
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [team]);

  const loadOptions = (inputValue, callback) => {
    if (prevSearch) {
      clearTimeout(prevSearch);
    }
    prevSearch = setTimeout(async () => {
      const search = inputValue.length > 0 ? inputValue.toLowerCase() : '';
      const requestResults = inputValue.length > 0 ? userList.filter(searchFilter(search)) : [];
      const searchResults = requestResults
        .map(({ firstName, lastName, email }) => ({
          value: email,
          label: `${lastName}, ${firstName} (${email})`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      callback(searchResults);
    }, 500);
  };

  const parsedValue = useMemo(() => {
    if (typeof value === 'string' && !isMulti) {
      return teamOptions.find(opt => opt.value === value) ?? { label: value, value };
    }
    return value;
  }, [isMulti, value, teamOptions]);

  return (
    <AsyncCreatableSelect
      menuPlacement={menuPlacement}
      menuPortalTarget={menuPortalTarget}
      isMulti={isMulti}
      isClearable={isClearable}
      cacheOptions
      value={parsedValue}
      placeholder="Select Contact"
      defaultOptions={teamOptions}
      loadOptions={loadOptions}
      onChange={(selected: OnChangeValue<any, boolean>, actionMeta) => {
        updateCachedOptions(selected);
        onChange(selected, actionMeta);
      }}
      onBlur={onBlur}
      isDisabled={disabled}
      formatCreateLabel={val => `Add Email ${val}`}
      styles={customStyles}
    />
  );
};

export default ContactSelect;
